import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useTodos } from '../../../context/TodoContext/TodoContext';
import { Todo } from '../../../types/types';

export const useTodoDetail = (id: string | undefined | null) => {
    const { todosMap, editTodo } = useTodos();
    const todo = id ? todosMap.get(id) : undefined;

    const [isEditing, setIsEditing] = useState(false);
    const [editedText, setEditedText] = useState(todo?.text || '');
    const [editedPriority, setEditedPriority] = useState(todo?.priority || 'medium');
    const [editedDueDate, setEditedDueDate] = useState<Dayjs | null>(
        todo?.dueDate ? dayjs(todo.dueDate) : null
    );
    const [editedDescription, setEditedDescription] = useState(todo?.description || '');

    const timeoutRef = useRef<NodeJS.Timeout>();

    const resetForm = useCallback(() => {
        setIsEditing(false);
        if (!todo) return;
        setEditedText(todo.text);
        setEditedPriority(todo.priority);
        setEditedDueDate(todo.dueDate ? dayjs(todo.dueDate) : null);
        setEditedDescription(todo.description || '');
    }, [todo]);

    useEffect(() => {
        resetForm();
    }, [resetForm]);

    const handleSaveChanges = () => {
        if (!todo || !id) return;

        const updatedTodo: Todo = {
            ...todo,
            text: editedText,
            priority: editedPriority,
            dueDate: editedDueDate ? editedDueDate : undefined,
            description: editedDescription,
            updatedAt: dayjs()
        };

        editTodo(id, updatedTodo);
        setIsEditing(false);
    };

    const updateDescription = async (description: string) => {
        if (!todo || !id) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        const updatedTodo: Todo = {
            ...todo,
            description: description,
            updatedAt: dayjs()
        };
        setEditedDescription(description);

        timeoutRef.current = setTimeout(() => {
            editTodo(id, updatedTodo);
            timeoutRef.current = undefined;
        }, 0);
    };

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleComplete = () => {
        if (!todo || !id) return;

        const updatedTodo: Todo = {
            ...todo,
            completed: !todo.completed,
            updatedAt: dayjs()
        };

        editTodo(id, updatedTodo);
    };

    return {
        todo,
        isEditing,
        setIsEditing,
        editedText,
        setEditedText,
        editedPriority,
        setEditedPriority,
        editedDueDate,
        setEditedDueDate,
        editedDescription,
        setEditedDescription,
        handleSaveChanges,
        updateDescription,
        handleComplete,
        resetForm
    };
}; 